import React, { useEffect, useState } from 'react';
import {
  Container,
  Typography,
  Box,
  Paper,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Chip,
  IconButton,
  Button,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogContentText,
  DialogActions,
  Snackbar,
  Alert,
  CircularProgress,
  Tooltip,
  TextField,
} from '@mui/material';
import {
  Edit as EditIcon,
  Delete as DeleteIcon,
  Star as StarIcon,
  StarBorder as StarBorderIcon,
  Schedule as ScheduleIcon,
} from '@mui/icons-material';
import { useNavigate } from 'react-router-dom';
import api from '../services/api';

interface Agency {
  _id?: string;
  id?: string;
  name: string;
  email: string;
  slug?: string;
  subscriptionPlan: string;
  subscriptionStatus: 'active' | 'trial' | 'expired' | 'pending_activation' | 'inactive' | 'suspended' | 'canceled' | 'cancelled';
  trialEndDate?: string;
  subscriptionEndDate?: string;
  isActive: boolean;
  seatLimit?: number;
  seatsUsed?: number;
}

const AdminAgencies = () => {
  const [agencies, setAgencies] = useState<Agency[]>([]);
  const [loading, setLoading] = useState(true);
  const [initialError, setInitialError] = useState<string | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [success, setSuccess] = useState<string | null>(null);
  const [assigningDemo, setAssigningDemo] = useState<string | null>(null);
  const [removingDemo, setRemovingDemo] = useState<string | null>(null);
  const [demoDialogOpen, setDemoDialogOpen] = useState(false);
  const [removeDemoDialogOpen, setRemoveDemoDialogOpen] = useState(false);
  const [extendTrialDialogOpen, setExtendTrialDialogOpen] = useState(false);
  const [extendingTrial, setExtendingTrial] = useState<string | null>(null);
  const [extendTrialDays, setExtendTrialDays] = useState<number | ''>('');
  const [extendTrialCustomDate, setExtendTrialCustomDate] = useState('');
  const [selectedAgency, setSelectedAgency] = useState<Agency | null>(null);
  const navigate = useNavigate();

  useEffect(() => {
    fetchAgencies();
  }, []);

  const fetchAgencies = async () => {
    try {
      setLoading(true);
      setInitialError(null);
      const response = await api.get('/admin/agencies');
      setAgencies(response.data);
    } catch (err: any) {
      setInitialError(err.response?.data?.message || 'Failed to fetch agencies');
    } finally {
      setLoading(false);
    }
  };

  const handleAssignDemo = (agency: Agency) => {
    setSelectedAgency(agency);
    setDemoDialogOpen(true);
  };

  const confirmAssignDemo = async () => {
    if (!selectedAgency) return;

    const agencyId = selectedAgency._id || selectedAgency.id;
    if (!agencyId) {
      setError('Agency ID not found');
      setDemoDialogOpen(false);
      return;
    }

    try {
      setAssigningDemo(agencyId);
      setError(null);
      
      await api.patch(`/admin/agencies/${agencyId}/subscription`, {
        planName: 'Demo'
      });

      setSuccess(`Successfully assigned Demo type to ${selectedAgency.name}`);
      setDemoDialogOpen(false);
      setSelectedAgency(null);
      await fetchAgencies(); // Refresh the list
    } catch (err: any) {
      setError(err.response?.data?.message || 'Failed to assign demo type');
    } finally {
      setAssigningDemo(null);
    }
  };

  const handleCloseDemoDialog = () => {
    if (!assigningDemo) {
      setDemoDialogOpen(false);
      setSelectedAgency(null);
    }
  };

  const handleRemoveDemo = (agency: Agency) => {
    setSelectedAgency(agency);
    setRemoveDemoDialogOpen(true);
  };

  const confirmRemoveDemo = async () => {
    if (!selectedAgency) return;

    const agencyId = selectedAgency._id || selectedAgency.id;
    if (!agencyId) {
      setError('Agency ID not found');
      setRemoveDemoDialogOpen(false);
      return;
    }

    try {
      setRemovingDemo(agencyId);
      setError(null);

      await api.patch(`/admin/agencies/${agencyId}/subscription`, {
        planName: 'Free',
      });

      setSuccess(`Successfully removed Demo type from ${selectedAgency.name}`);
      setRemoveDemoDialogOpen(false);
      setSelectedAgency(null);
      await fetchAgencies();
    } catch (err: any) {
      setError(err.response?.data?.message || 'Failed to remove demo type');
    } finally {
      setRemovingDemo(null);
    }
  };

  const handleCloseRemoveDemoDialog = () => {
    if (!removingDemo) {
      setRemoveDemoDialogOpen(false);
      setSelectedAgency(null);
    }
  };

  const handleOpenExtendTrial = (agency: Agency) => {
    setSelectedAgency(agency);
    setExtendTrialDays('');
    setExtendTrialCustomDate('');
    setExtendTrialDialogOpen(true);
  };

  const handleCloseExtendTrialDialog = () => {
    if (!extendingTrial) {
      setExtendTrialDialogOpen(false);
      setSelectedAgency(null);
      setExtendTrialDays('');
      setExtendTrialCustomDate('');
    }
  };

  const confirmExtendTrial = async () => {
    if (!selectedAgency) return;

    const agencyId = selectedAgency._id || selectedAgency.id;
    if (!agencyId) {
      setError('Agency ID not found');
      return;
    }

    let payload: { trialEndDate?: string; trialExtensionDays?: number } = {};
    if (extendTrialCustomDate) {
      payload.trialEndDate = new Date(extendTrialCustomDate).toISOString();
    } else if (typeof extendTrialDays === 'number' && extendTrialDays > 0) {
      payload.trialExtensionDays = extendTrialDays;
    } else {
      setError('Please choose extend by days or pick a custom date');
      return;
    }

    try {
      setExtendingTrial(agencyId);
      setError(null);

      await api.patch(`/admin/agencies/${agencyId}/subscription`, payload);

      setSuccess(`Trial extended for ${selectedAgency.name}`);
      setExtendTrialDialogOpen(false);
      setSelectedAgency(null);
      setExtendTrialDays('');
      setExtendTrialCustomDate('');
      await fetchAgencies();
    } catch (err: any) {
      setError(err.response?.data?.message || 'Failed to extend trial');
    } finally {
      setExtendingTrial(null);
    }
  };

  const isOnTrial = (agency: Agency) =>
    agency.subscriptionStatus === 'trial' || agency.subscriptionStatus === 'trialing';

  const getStatusColor = (status: Agency['subscriptionStatus']) => {
    switch (status) {
      case 'active':
        return 'success';
      case 'trial':
        return 'warning';
      case 'expired':
        return 'error';
      default:
        return 'default';
    }
  };

  const formatDate = (dateString?: string) => {
    if (!dateString) return 'N/A';
    return new Date(dateString).toLocaleDateString('en-AU', { day: '2-digit', month: '2-digit', year: 'numeric' });
  };

  if (loading) {
    return (
      <Container>
        <Box sx={{ mt: 4, textAlign: 'center' }}>
          <Typography>Loading agencies...</Typography>
        </Box>
      </Container>
    );
  }

  if (initialError) {
    return (
      <Container>
        <Box sx={{ mt: 4, textAlign: 'center' }}>
          <Typography color="error">{initialError}</Typography>
        </Box>
      </Container>
    );
  }

  return (
    <Container maxWidth="lg">
      <Box sx={{ mt: 4, mb: 4 }}>
        <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 3 }}>
          <Typography variant="h4" component="h1">
            Agencies
          </Typography>
        </Box>

        <TableContainer component={Paper}>
          <Table>
            <TableHead>
              <TableRow>
                <TableCell>Agency Name</TableCell>
                <TableCell>Email</TableCell>
                <TableCell>Plan</TableCell>
                <TableCell>Status</TableCell>
                <TableCell>Trial Ends</TableCell>
                <TableCell>Subscription Ends</TableCell>
                <TableCell>Seats</TableCell>
                <TableCell>Active</TableCell>
                <TableCell>Actions</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {agencies.map((agency) => {
                const agencyId = agency._id || agency.id;
                const isDemo = agency.subscriptionPlan === 'Demo';
                const isAssigning = assigningDemo === agencyId;
                const isRemoving = removingDemo === agencyId;
                const onTrial = isOnTrial(agency);
                
                return (
                  <TableRow key={agencyId}>
                    <TableCell>{agency.name}</TableCell>
                    <TableCell>{agency.email}</TableCell>
                    <TableCell>
                      {isDemo ? (
                        <Chip
                          label="Demo"
                          color="primary"
                          size="small"
                          icon={<StarIcon />}
                        />
                      ) : (
                        agency.subscriptionPlan
                      )}
                    </TableCell>
                    <TableCell>
                      <Chip
                        label={agency.subscriptionStatus}
                        color={getStatusColor(agency.subscriptionStatus)}
                        size="small"
                      />
                    </TableCell>
                    <TableCell>{formatDate(agency.trialEndDate)}</TableCell>
                    <TableCell>{formatDate(agency.subscriptionEndDate)}</TableCell>
                    <TableCell>
                      {agency.seatsUsed != null && agency.seatLimit != null
                        ? `${agency.seatsUsed} / ${agency.seatLimit >= 999999 ? 'Unlimited' : agency.seatLimit}`
                        : agency.seatsUsed != null
                          ? String(agency.seatsUsed)
                          : '—'}
                    </TableCell>
                    <TableCell>
                      <Chip
                        label={agency.isActive ? 'Yes' : 'No'}
                        color={agency.isActive ? 'success' : 'error'}
                        size="small"
                      />
                    </TableCell>
                    <TableCell>
                      <Box sx={{ display: 'flex', gap: 0.5, alignItems: 'center' }}>
                        {onTrial && (
                          <Tooltip title="Extend Trial">
                            <IconButton
                              size="small"
                              onClick={() => handleOpenExtendTrial(agency)}
                              disabled={!!extendingTrial}
                              color="secondary"
                            >
                              {extendingTrial === agencyId ? (
                                <CircularProgress size={20} />
                              ) : (
                                <ScheduleIcon />
                              )}
                            </IconButton>
                          </Tooltip>
                        )}
                        {!isDemo && (
                          <Tooltip title="Assign Demo Type (All Features)">
                            <IconButton
                              size="small"
                              onClick={() => handleAssignDemo(agency)}
                              disabled={isAssigning || !!removingDemo}
                              color="primary"
                            >
                              {isAssigning ? (
                                <CircularProgress size={20} />
                              ) : (
                                <StarIcon />
                              )}
                            </IconButton>
                          </Tooltip>
                        )}
                        {isDemo && (
                          <Tooltip title="Remove Demo Type">
                            <IconButton
                              size="small"
                              onClick={() => handleRemoveDemo(agency)}
                              disabled={isRemoving || !!assigningDemo}
                              color="warning"
                            >
                              {isRemoving ? (
                                <CircularProgress size={20} />
                              ) : (
                                <StarBorderIcon />
                              )}
                            </IconButton>
                          </Tooltip>
                        )}
                      </Box>
                    </TableCell>
                  </TableRow>
                );
              })}
            </TableBody>
          </Table>
        </TableContainer>
      </Box>

      {/* Extend Trial Dialog */}
      <Dialog
        open={extendTrialDialogOpen}
        onClose={handleCloseExtendTrialDialog}
        maxWidth="xs"
        fullWidth
      >
        <DialogTitle>Extend Trial</DialogTitle>
        <DialogContent>
          <DialogContentText sx={{ mb: 2 }}>
            Extend trial for <strong>{selectedAgency?.name}</strong>
            {selectedAgency?.trialEndDate && (
              <> (currently ends {formatDate(selectedAgency.trialEndDate)})</>
            )}.
          </DialogContentText>
          <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
            <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1 }}>
              {[7, 14, 30].map((days) => (
                <Button
                  key={days}
                  variant={extendTrialDays === days ? 'contained' : 'outlined'}
                  size="small"
                  onClick={() => {
                    setExtendTrialDays(days);
                    setExtendTrialCustomDate('');
                  }}
                >
                  +{days} days
                </Button>
              ))}
            </Box>
            <TextField
              label="Or pick custom date"
              type="date"
              value={extendTrialCustomDate}
              onChange={(e) => {
                setExtendTrialCustomDate(e.target.value);
                setExtendTrialDays('');
              }}
              InputLabelProps={{ shrink: true }}
              fullWidth
            />
          </Box>
        </DialogContent>
        <DialogActions>
          <Button onClick={handleCloseExtendTrialDialog} disabled={!!extendingTrial}>
            Cancel
          </Button>
          <Button
            onClick={confirmExtendTrial}
            color="primary"
            variant="contained"
            disabled={!!extendingTrial}
          >
            {extendingTrial ? 'Extending...' : 'Extend Trial'}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Demo Assignment Confirmation Dialog */}
      <Dialog
        open={demoDialogOpen}
        onClose={handleCloseDemoDialog}
        aria-labelledby="demo-dialog-title"
        aria-describedby="demo-dialog-description"
      >
        <DialogTitle id="demo-dialog-title">
          Assign Demo Type
        </DialogTitle>
        <DialogContent>
          <DialogContentText id="demo-dialog-description">
            Are you sure you want to assign <strong>Demo</strong> type to <strong>{selectedAgency?.name}</strong>?
            <br /><br />
            This will:
            <ul>
              <li>Grant access to all features</li>
              <li>Set limits: 5 seats, 1GB storage</li>
              <li>Set subscription status to active</li>
            </ul>
          </DialogContentText>
        </DialogContent>
        <DialogActions>
          <Button onClick={handleCloseDemoDialog} disabled={!!assigningDemo}>
            Cancel
          </Button>
          <Button
            onClick={confirmAssignDemo}
            color="primary"
            variant="contained"
            disabled={!!assigningDemo}
            startIcon={assigningDemo ? <CircularProgress size={16} /> : <StarIcon />}
          >
            {assigningDemo ? 'Assigning...' : 'Assign Demo Type'}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Demo Removal Confirmation Dialog */}
      <Dialog
        open={removeDemoDialogOpen}
        onClose={handleCloseRemoveDemoDialog}
        aria-labelledby="remove-demo-dialog-title"
        aria-describedby="remove-demo-dialog-description"
      >
        <DialogTitle id="remove-demo-dialog-title">
          Remove Demo Type
        </DialogTitle>
        <DialogContent>
          <DialogContentText id="remove-demo-dialog-description">
            Are you sure you want to remove <strong>Demo</strong> type from <strong>{selectedAgency?.name}</strong>?
            <br /><br />
            This will:
            <ul>
              <li>Revert the agency to the Free plan</li>
              <li>Restore Free plan seat, storage, and feature limits</li>
              <li>Keep subscription status active</li>
            </ul>
          </DialogContentText>
        </DialogContent>
        <DialogActions>
          <Button onClick={handleCloseRemoveDemoDialog} disabled={!!removingDemo}>
            Cancel
          </Button>
          <Button
            onClick={confirmRemoveDemo}
            color="warning"
            variant="contained"
            disabled={!!removingDemo}
            startIcon={removingDemo ? <CircularProgress size={16} /> : <StarBorderIcon />}
          >
            {removingDemo ? 'Removing...' : 'Remove Demo Type'}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Success Snackbar */}
      <Snackbar
        open={!!success}
        autoHideDuration={6000}
        onClose={() => setSuccess(null)}
        anchorOrigin={{ vertical: 'top', horizontal: 'right' }}
      >
        <Alert onClose={() => setSuccess(null)} severity="success" sx={{ width: '100%' }}>
          {success}
        </Alert>
      </Snackbar>

      {/* Error Snackbar */}
      <Snackbar
        open={!!error}
        autoHideDuration={6000}
        onClose={() => setError(null)}
        anchorOrigin={{ vertical: 'top', horizontal: 'right' }}
      >
        <Alert onClose={() => setError(null)} severity="error" sx={{ width: '100%' }}>
          {error}
        </Alert>
      </Snackbar>
    </Container>
  );
};

export default AdminAgencies; 